import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { User } from '../types';
import { getCurrentUser, completeReferralSignup } from '../api/api';
import { jwtDecode } from 'jwt-decode';
import { useSnackbar } from './snackbarContext';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    isOptimistic: boolean;
    login: (token: string, refreshToken?: string) => void;
    logout: () => void;
    updatePreferredPrompt: (promptId: string) => void;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    isAuthenticated: false,
    isOptimistic: false,
    login: () => { },
    logout: () => { },
    updatePreferredPrompt: () => { },
});

export const useAuth = () => useContext(AuthContext);

// Auto-refresh utility
const refreshTokens = async (): Promise<{ token: string; refreshToken: string } | null> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': import.meta.env.VITE_API_KEY || '',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isOptimistic, setIsOptimistic] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Add refs to prevent duplicate side effects under React StrictMode
    const referralCompletedRef = useRef(false);
    const hasLoadedUserRef = useRef(false);

    // Check if token needs refresh (within 24 hours of expiration)
    const shouldRefreshToken = (token: string): boolean => {
        try {
            const decoded: any = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decoded.exp - currentTime;
            // Refresh if token expires within 24 hours (86400 seconds)
            return timeUntilExpiry < 86400;
        } catch {
            return true; // If we can't decode, assume we need to refresh
        }
    };

    // Referral completion utility
    const completeReferralIfPresent = useCallback(async (userId: string) => {
        // Prevent multiple referral completions
        if (referralCompletedRef.current) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔗 AuthContext: Referral already completed, skipping duplicate execution', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    timestamp: new Date().toISOString()
                });
            }
            return;
        }

        const referralCode = sessionStorage.getItem('referralCode');
        const referralInfo = sessionStorage.getItem('referralInfo');

        if (!referralCode || !referralInfo) {
            return;
        }

        try {
            // Mark referral as being processed to prevent duplicates
            referralCompletedRef.current = true;

            // Additional validation: Check if this is a self-referral
            if (referralInfo) {
                try {
                    const parsedInfo = JSON.parse(referralInfo);
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔗 AuthContext: Parsed referral info for validation', {
                            service: 'montessori-shopping-list-frontend',
                            environment: process.env.NODE_ENV,
                            runtime: 'browser',
                            userId,
                            referralCode,
                            parsedReferralInfo: parsedInfo,
                            inviterUserId: parsedInfo.inviterUserId,
                            isSelfReferral: parsedInfo.inviterUserId === userId,
                            timestamp: new Date().toISOString()
                        });
                    }

                    if (parsedInfo.inviterUserId === userId) {
                        console.warn('🔗 AuthContext: Self-referral detected, preventing completion', {
                            service: 'montessori-shopping-list-frontend',
                            environment: process.env.NODE_ENV,
                            runtime: 'browser',
                            referralCode,
                            inviterUserId: parsedInfo.inviterUserId,
                            timestamp: new Date().toISOString()
                        });

                        // Clear referral data and show warning
                        sessionStorage.removeItem('referralCode');
                        sessionStorage.removeItem('referralInfo');

                        console.log('🔗 AuthContext: Self-referral data cleared from session storage', {
                            service: 'montessori-shopping-list-frontend',
                            environment: process.env.NODE_ENV,
                            runtime: 'browser',
                            userId,
                            referralCode,
                            timestamp: new Date().toISOString()
                        });

                        return;
                    }
                } catch (parseError) {
                    console.warn('🔗 AuthContext: Could not parse referral info for self-referral check', {
                        service: 'montessori-shopping-list-frontend',
                        environment: process.env.NODE_ENV,
                        runtime: 'browser',
                        userId,
                        referralCode,
                        error: parseError instanceof Error ? parseError.message : 'Unknown error',
                        timestamp: new Date().toISOString()
                    });
                }
            }

            console.log('🔗 AuthContext: Completing referral signup', {
                service: 'montessori-shopping-list-frontend',
                environment: process.env.NODE_ENV,
                runtime: 'browser',
                userId,
                referralCode,
                hasReferralInfo: !!referralInfo,
                timestamp: new Date().toISOString()
            });

            const result = await completeReferralSignup(referralCode, userId);

            console.log('🔗 AuthContext: Referral completion successful', {
                service: 'montessori-shopping-list-frontend',
                environment: process.env.NODE_ENV,
                runtime: 'browser',
                userId,
                referralCode,
                result,
                timestamp: new Date().toISOString()
            });

            // Invalidate user limits query to refresh plan indicator
            queryClient.invalidateQueries({ queryKey: ['user-limits', userId] });
            queryClient.invalidateQueries({ queryKey: ['subscription'] });

            // Clear referral data after successful completion
            sessionStorage.removeItem('referralCode');
            sessionStorage.removeItem('referralInfo');

            console.log('🔗 AuthContext: Referral data cleared from session storage', {
                service: 'montessori-shopping-list-frontend',
                environment: process.env.NODE_ENV,
                runtime: 'browser',
                userId,
                referralCode,
                timestamp: new Date().toISOString()
            });

        } catch (error: any) {
            console.error('🔗 AuthContext: Failed to complete referral signup', {
                service: 'montessori-shopping-list-frontend',
                environment: process.env.NODE_ENV,
                runtime: 'browser',
                userId,
                referralCode,
                error: error instanceof Error ? error.message : 'Unknown error',
                timestamp: new Date().toISOString()
            });

            // Handle specific referral errors
            if (error?.response?.data?.error?.code === 'REFERRAL_ALREADY_COMPLETED') {
                // This is a permanent failure - clear referral data and show user-friendly message
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                // Show user-friendly message using snackbar
                showSnackbar(t('account.referral.already_completed'), 'warning');

                if (process.env.NODE_ENV === 'development') {
                    console.log('🔗 AuthContext: Referral already completed - data cleared', {
                        service: 'montessori-shopping-list-frontend',
                        environment: process.env.NODE_ENV,
                        runtime: 'browser',
                        userId,
                        referralCode,
                        timestamp: new Date().toISOString()
                    });
                }
            } else if (error?.response?.data?.error?.code === 'REFERRAL_NOT_FOUND') {
                // Invalid referral code - clear data and show error
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar('Invalid referral code. Please check the link and try again.', 'error');

                console.log('🔗 AuthContext: Invalid referral code - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.data?.error?.code === 'SELF_REFERRAL_NOT_ALLOWED') {
                // Self-referral detected - clear data and show warning
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar('You cannot refer yourself. Please use a different referral link.', 'warning');

                console.log('🔗 AuthContext: Self-referral detected - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.status === 429) {
                // Rate limited - don't clear data, let user retry later
                showSnackbar('Too many referral attempts. Please try again later.', 'warning');

                console.log('🔗 AuthContext: Rate limited - keeping referral data for retry', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.data?.error?.code === 'INVITER_BANNED') {
                // Inviter is banned - clear data and show error
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar('This referral link is no longer valid. Please use a different one.', 'error');

                console.log('🔗 AuthContext: Inviter banned - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.data?.error?.code === 'REFERRAL_OWNERSHIP_MISMATCH') {
                // Ownership mismatch - clear data and show error
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar('Invalid referral link. Please use a different one.', 'error');

                console.log('🔗 AuthContext: Ownership mismatch - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.data?.error?.code === 'UNAUTHORIZED_REFERRAL_COMPLETION') {
                // Unauthorized completion - clear data and show error
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar('You are not authorized to complete this referral.', 'error');

                console.log('🔗 AuthContext: Unauthorized completion - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else if (error?.response?.data?.error?.code === 'EXISTING_USER_REFERRAL_NOT_ALLOWED') {
                // Existing user trying to use referral code - clear data and show error
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');

                showSnackbar(t('account.referral.existing_user_not_allowed'), 'warning');

                console.log('🔗 AuthContext: Existing user referral attempt - data cleared', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    timestamp: new Date().toISOString()
                });
            } else {
                // Generic error - don't clear referral data on other failures - let user retry
                // This could happen if the API is temporarily unavailable
                showSnackbar('Failed to complete referral. Please try again later.', 'error');

                console.log('🔗 AuthContext: Generic referral error - keeping data for retry', {
                    service: 'montessori-shopping-list-frontend',
                    environment: process.env.NODE_ENV,
                    runtime: 'browser',
                    userId,
                    referralCode,
                    error: error?.response?.data?.error?.code || 'unknown',
                    timestamp: new Date().toISOString()
                });
            }
        }
    }, [showSnackbar, t, queryClient]);

    useEffect(() => {
        if (hasLoadedUserRef.current) {
            return;
        }
        hasLoadedUserRef.current = true;

        const loadUser = async () => {
            const token = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refreshToken');

            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // Check if token is expired or close to expiring
                let decoded: any;
                try {
                    decoded = jwtDecode<User>(token);
                } catch (jwtError) {
                    // If token is not a valid JWT (e.g., dev-test-token), skip optimistic loading
                    // and go directly to fetching user data from backend
                    console.log('Token is not a JWT, fetching user data from backend');
                    const userData = await getCurrentUser();
                    setUser(userData);
                    queryClient.setQueryData<User | null>(['user'], userData);
                    setIsOptimistic(false);
                    setLoading(false);
                    return;
                }

                const currentTime = Date.now() / 1000;

                // OPTIMIZATION: Set optimistic user data immediately for faster UI
                if (decoded.exp > currentTime) {
                    const optimisticUser = {
                        id: decoded.id,
                        name: decoded.name,
                        email: decoded.email,
                        createdAt: Date.now()
                    };
                    setUser(optimisticUser);
                    setIsOptimistic(true);
                    setLoading(false); // Show UI immediately
                }

                if (decoded.exp < currentTime) {
                    // Token is expired, try to refresh
                    if (refreshToken) {
                        console.log('Token expired, attempting refresh...');
                        const newTokens = await refreshTokens();
                        if (newTokens) {
                            localStorage.setItem('token', newTokens.token);
                            localStorage.setItem('refreshToken', newTokens.refreshToken);
                            const newDecoded = jwtDecode<User>(newTokens.token);
                            const refreshedUser: User = {
                                id: newDecoded.id,
                                name: newDecoded.name,
                                email: newDecoded.email,
                                createdAt: Date.now() // Use current timestamp as fallback
                            };
                            setUser(refreshedUser);
                            queryClient.setQueryData<User | null>(['user'], refreshedUser);
                            console.log('Token refreshed successfully');
                        } else {
                            // Refresh failed, clear everything
                            localStorage.removeItem('token');
                            localStorage.removeItem('refreshToken');
                            setUser(null);
                            queryClient.removeQueries({ queryKey: ['user'] });
                            setIsOptimistic(false);
                        }
                    } else {
                        localStorage.removeItem('token');
                        setUser(null);
                        queryClient.removeQueries({ queryKey: ['user'] });
                        setIsOptimistic(false);
                    }
                    setLoading(false);
                    return;
                } else if (shouldRefreshToken(token) && refreshToken) {
                    // Token is still valid but should be refreshed proactively
                    console.log('Proactively refreshing token...');
                    const newTokens = await refreshTokens();
                    if (newTokens) {
                        localStorage.setItem('token', newTokens.token);
                        localStorage.setItem('refreshToken', newTokens.refreshToken);
                        const refreshedDecoded = jwtDecode<User>(newTokens.token);
                        const refreshedUser: User = {
                            id: refreshedDecoded.id,
                            name: refreshedDecoded.name,
                            email: refreshedDecoded.email,
                            createdAt: Date.now()
                        };
                        setUser(refreshedUser);
                        queryClient.setQueryData<User | null>(['user'], refreshedUser);
                        console.log('Token proactively refreshed');
                    }
                }

                // Load user data - now optimized for caching
                // User profile data is cached for 7 days, so this should be fast
                const userData = await getCurrentUser();
                setUser(userData);
                queryClient.setQueryData<User | null>(['user'], userData);
                setIsOptimistic(false); // No longer optimistic

                // Complete referral if present after user is loaded
                if (userData?.id) {
                    // Use a small delay to ensure login state is fully established
                    setTimeout(async () => {
                        try {
                            await completeReferralIfPresent(userData.id);
                        } catch (error) {
                            console.warn('Referral completion failed after user load:', error);
                        }
                    }, 100);
                }

            } catch (error) {
                console.error('Error loading user:', error);
                // If getCurrentUser fails, try refreshing tokens first
                if (refreshToken) {
                    const newTokens = await refreshTokens();
                    if (newTokens) {
                        localStorage.setItem('token', newTokens.token);
                        localStorage.setItem('refreshToken', newTokens.refreshToken);
                        try {
                            // Retry loading user data after token refresh - should be fast due to caching
                            const userData = await getCurrentUser();
                            setUser(userData);
                            queryClient.setQueryData<User | null>(['user'], userData);
                            setIsOptimistic(false); // No longer optimistic

                            // Complete referral if present after user is loaded
                            if (userData?.id) {
                                // Use a small delay to ensure login state is fully established
                                setTimeout(async () => {
                                    try {
                                        await completeReferralIfPresent(userData.id);
                                    } catch (error) {
                                        console.warn('Referral completion failed after retry:', error);
                                    }
                                }, 100);
                            }

                        } catch (retryError) {
                            console.error('Retry after refresh failed:', retryError);
                            localStorage.removeItem('token');
                            localStorage.removeItem('refreshToken');
                            setUser(null);
                            queryClient.removeQueries({ queryKey: ['user'] });
                            setIsOptimistic(false);
                        }
                    } else {
                        localStorage.removeItem('token');
                        localStorage.removeItem('refreshToken');
                        setUser(null);
                        queryClient.removeQueries({ queryKey: ['user'] });
                        setIsOptimistic(false);
                    }
                } else {
                    localStorage.removeItem('token');
                    setUser(null);
                    queryClient.removeQueries({ queryKey: ['user'] });
                    setIsOptimistic(false);
                }
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, []);

    // Set up automatic token refresh interval
    useEffect(() => {
        const interval = setInterval(async () => {
            const token = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refreshToken');

            if (token && refreshToken && shouldRefreshToken(token)) {
                console.log('Auto-refreshing token...');
                const newTokens = await refreshTokens();
                if (newTokens) {
                    localStorage.setItem('token', newTokens.token);
                    localStorage.setItem('refreshToken', newTokens.refreshToken);
                    const refreshedDecoded = jwtDecode<User>(newTokens.token);
                    const refreshedUser: User = {
                        id: refreshedDecoded.id,
                        name: refreshedDecoded.name,
                        email: refreshedDecoded.email,
                        createdAt: Date.now()
                    };
                    setUser(refreshedUser);
                    queryClient.setQueryData<User | null>(['user'], refreshedUser);
                    console.log('Token auto-refreshed successfully');
                }
            }
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, []);

    const login = useCallback(async (token: string, refreshToken?: string) => {
        localStorage.setItem('token', token);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }

        if (token) {
            try {
                const decoded = jwtDecode<User>(token);
                // Set initial user data from token
                const initialUser = {
                    id: decoded.id,
                    name: decoded.name,
                    email: decoded.email,
                    createdAt: Date.now() // Use current timestamp as fallback
                };
                setUser(initialUser);

                // Immediately fetch complete user data including avatarUrl
                // This should be fast due to service worker caching (7 days for user profile)
                try {
                    const completeUserData = await getCurrentUser();
                    setUser(completeUserData);
                    queryClient.setQueryData<User | null>(['user'], completeUserData);

                    // Complete referral if present after user is loaded
                    if (completeUserData?.id) {
                        // Use a small delay to ensure login state is fully established
                        setTimeout(async () => {
                            try {
                                await completeReferralIfPresent(completeUserData.id);
                            } catch (error) {
                                console.warn('Referral completion failed after login:', error);
                            }
                        }, 100);
                    }

                } catch (error) {
                    console.warn('Failed to fetch complete user data on login:', error);
                    // Keep the initial user data if complete fetch fails

                    // Still try to complete referral with initial user data
                    if (initialUser?.id) {
                        await completeReferralIfPresent(initialUser.id);
                    }
                }
            } catch (e) {
                console.error('Invalid token', e);
            }
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
        queryClient.removeQueries({ queryKey: ['user'] });
    }, [queryClient]);

    const updatePreferredPrompt = useCallback((promptId: string) => {
        setUser((prev) => {
            if (!prev) return prev;
            const nextUser = { ...prev, preferredPromptId: promptId };
            queryClient.setQueryData<User | null>(['user'], nextUser);
            return nextUser;
        });
    }, [queryClient]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated: !!user,
                isOptimistic,
                login,
                logout,
                updatePreferredPrompt
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
